/**
 * Picking a timezone.
 *
 * The list comes from the browser's own IANA database when it offers one, so
 * it never goes stale as governments move their clocks around. Older engines
 * without `supportedValuesOf` fall back to a short list covering the places
 * this business actually operates — a wrong-but-present dropdown beats an
 * empty one.
 */

const FALLBACK_ZONES = [
  'Asia/Karachi',
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Singapore',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'UTC',
];

export function timeZones(): string[] {
  const supported = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  if (typeof supported === 'function') {
    try {
      return supported('timeZone');
    } catch {
      // Fall through to the short list.
    }
  }
  return FALLBACK_ZONES;
}

export function detectedTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/**
 * Every zone with its offset, computed once for the life of the page.
 *
 * Labelling 418 zones costs two Intl.DateTimeFormat constructions each, and
 * it was happening inside the render — so every keystroke anywhere on the
 * profile page paid 836 of them. Measured at 157ms per render, which is
 * exactly the lag between pressing a key and seeing the letter.
 *
 * Safe to cache: an offset changes when a government moves the clocks, not
 * while somebody is filling in a form.
 */
let labelled: { zone: string; label: string }[] | null = null;

function labelledZones(): { zone: string; label: string }[] {
  labelled ??= timeZones().map((zone) => {
    const offset = offsetLabel(zone);
    return {
      zone,
      label: offset ? `${zone.replace(/_/g, ' ')} (${offset})` : zone.replace(/_/g, ' '),
    };
  });
  return labelled;
}

/** `+05:00` — the offset a zone is on today, for the label. */
export function offsetLabel(zone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

export function TimeZoneSelect({
  id,
  value,
  onChange,
}: {
  id?: string;
  value: string;
  onChange: (zone: string) => void;
}) {
  const known = labelledZones();
  // A stored zone the browser has never heard of would otherwise vanish from
  // the list and silently reset the user's answer on the next save.
  const options = known.some((option) => option.zone === value)
    ? known
    : [{ zone: value, label: value.replace(/_/g, ' ') }, ...known];

  return (
    <select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-control w-full rounded-md border border-border-input bg-surface px-3 text-body-sm text-ink"
    >
      {options.map((option) => (
        <option key={option.zone} value={option.zone}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
